import type { FeeAdjustmentLineDraft } from "../../fee-adjustment";
import { feeAdjustmentSpecUnit, previewStructuredFeeAdjustment } from "../service";
import type {
  FulfillmentOrderLine,
  FulfillmentResourceMentionCandidate,
  FulfillmentResourceMentionLevel,
  FulfillmentResourceMentionType,
  FulfillmentResourceTransportMode,
  FulfillmentSupplierOption,
  FulfillmentTransportPoint,
  FulfillmentTransportResourceCandidate,
} from "../types";
import {
  FulfillmentOrderQuickEntryPanel,
  toFulfillmentOrderQuickEntryLinePatches,
  type FulfillmentOrderQuickEntrySubjects,
} from "./FulfillmentOrderQuickEntryPanel";
import type {
  ConfirmationEntry,
  ConfirmationEntryResourceType,
  ConfirmationItineraryDay,
} from "../service/confirmationEntryParser";

type Props = {
  disabled: boolean;
  workspaceStatus: string;
  itineraryDays: readonly ConfirmationItineraryDay[];
  serviceProviderOptions: readonly FulfillmentSupplierOption[];
  settlementSupplierOptions: readonly FulfillmentSupplierOption[];
  serviceProviderResourceType?: string | null;
  serviceProviderSearchKeyword?: string;
  serviceProviderLoading?: boolean;
  settlementSupplierLoading?: boolean;
  serviceProviderError?: unknown;
  settlementSupplierError?: unknown;
  serviceProviderErrorCode?: string | null;
  settlementSupplierErrorCode?: string | null;
  onRetryServiceProviders?: () => void;
  onRetrySettlementSuppliers?: () => void;
  onServiceProviderResourceTypeChange?: (resourceType: string) => void;
  onSearchServiceProviders?: (keyword: string) => void;
  onSearchSettlementSuppliers?: (keyword: string) => void;
  searchResourceCandidates?: (
    resourceType: FulfillmentResourceMentionType,
    level: FulfillmentResourceMentionLevel,
    keyword: string,
    spuId?: string | null,
    transportMode?: FulfillmentResourceTransportMode | null,
    fulfillmentSupplierId?: string | null,
  ) => Promise<FulfillmentResourceMentionCandidate[]>;
  searchTransportPoints?: (
    mode: FulfillmentResourceTransportMode,
    keyword: string,
  ) => Promise<FulfillmentTransportPoint[]>;
  searchTransportResourceCandidates?: (
    mode: FulfillmentResourceTransportMode,
    fromPointId: string,
    toPointId: string,
    specKeyword?: string | null,
  ) => Promise<FulfillmentTransportResourceCandidate[]>;
  onCreated: (lines: FeeAdjustmentLineDraft[]) => void;
  onPendingTextChange?: (pending: boolean) => void;
};

/** 生成仅用于本次编辑的草稿标识。 */
function draftId(): string {
  return `fee-adjustment-add-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`}`;
}

/** 将六类录入的资源选择转换为保存契约。 */
function resourceSelectionInput(
  value: FulfillmentOrderLine["resourceSelection"],
): Record<string, unknown> | null {
  if (!value) return null;
  if (value.kind === "stay") {
    return { kind: "stay", stay_spu_id: value.staySpuId, sku_name: value.skuName };
  }
  if (value.kind === "transport") {
    return {
      kind: "transport",
      transport_mode: value.transportMode,
      from_point_id: value.fromPointId,
      to_point_id: value.toPointId,
    };
  }
  if (value.kind === "bus") {
    return {
      kind: "bus",
      vehicle_spec_name: value.vehicleSpecName,
      entry_semantic: value.entrySemantic,
    };
  }
  return { kind: "point", point_spu_id: value.pointSpuId, sku_name: value.skuName };
}

/** 费用调整新增项目：复用确认件六类录入，但只生成本地草稿。 */
export function FulfillmentOrderFeeAdjustmentQuickEntry({
  disabled,
  workspaceStatus,
  itineraryDays,
  serviceProviderOptions,
  settlementSupplierOptions,
  serviceProviderResourceType,
  serviceProviderSearchKeyword = "",
  serviceProviderLoading = false,
  settlementSupplierLoading = false,
  serviceProviderError = null,
  settlementSupplierError = null,
  serviceProviderErrorCode = null,
  settlementSupplierErrorCode = null,
  onRetryServiceProviders,
  onRetrySettlementSuppliers,
  onServiceProviderResourceTypeChange,
  onSearchServiceProviders,
  onSearchSettlementSuppliers,
  searchResourceCandidates,
  searchTransportPoints,
  searchTransportResourceCandidates,
  onCreated,
  onPendingTextChange,
}: Props) {
  /** 完整计价和主体事实进入本地草稿，最终保存时统一落库。 */
  async function addAdjustmentEntries(
    entries: ConfirmationEntry[],
    subjects: FulfillmentOrderQuickEntrySubjects,
  ) {
    const settlementSupplier = subjects.settlementSupplier;
    if (!settlementSupplier) throw new Error("请先选择结算供应商");
    if (subjects.resourceType === "住宿" && !subjects.staySpu) {
      throw new Error("请先选择服务酒店");
    }
    if (subjects.resourceType !== "住宿" && !subjects.fulfillmentSupplier) {
      throw new Error("请先选择服务提供商");
    }
    const patches = toFulfillmentOrderQuickEntryLinePatches({ entries, subjects, workspaceStatus });
    onCreated(patches.map((patch, index) => {
      const entry = entries[index]!;
      const quantity = String(patch.quantity ?? entry.quantity);
      const billingCount = String(patch.billingCount ?? entry.billingCount);
      const unitPrice = String(patch.unitPriceAmount ?? entry.unitPrice);
      const specUnit = feeAdjustmentSpecUnit(entry.resourceType);
      const pricing = previewStructuredFeeAdjustment(
        {
          quantity: "0.00",
          billingCount: "0.00",
          unitPriceAmount: "0.00",
          amount: "0.00",
        },
        { quantity, billingCount, unitPriceAmount: unitPrice, specUnit },
      );
      if (!pricing.normalized || pricing.error) throw new Error(pricing.error ?? "费用计价无效");
      const provider = subjects.resourceType === "住宿"
        ? {
            type: "resource_spu" as const,
            id: subjects.staySpu!.spuId!,
            name: subjects.staySpu!.spuName!,
          }
        : {
            type: "supplier" as const,
            id: subjects.fulfillmentSupplier!.id,
            name: subjects.fulfillmentSupplier!.name,
          };
      return {
        draft_line_id: draftId(),
        adjustment_type: "add_service" as const,
        source_confirmation_line_id: null,
        item_name: String(patch.itemNameSnapshot ?? entry.itemName),
        item_spec: patch.itemSpecSnapshot ?? null,
        resource_type: entry.resourceType,
        spu_table_name: patch.spuTableName ?? null,
        spu_id: patch.spuId ?? null,
        sku_table_name: patch.skuTableName ?? null,
        sku_id: patch.skuId ?? null,
        resource_selection: resourceSelectionInput(patch.resourceSelection),
        service_provider_type: provider.type,
        service_provider_id: provider.id,
        service_provider_name: provider.name,
        settlement_supplier_id: settlementSupplier.id,
        settlement_supplier_name: settlementSupplier.name,
        procurement_channel_type: patch.procurementChannelType ?? "unknown",
        procurement_source_type: patch.procurementSourceType ?? null,
        procurement_source_id: patch.procurementSourceId ?? null,
        provider_assignment_status: patch.providerAssignmentStatus ?? "not_applicable",
        day_expression: patch.dayExpression ?? entry.dayExpression,
        day_numbers: patch.dayNumbers ?? entry.dayNumbers,
        service_dates: patch.serviceDates ?? entry.serviceDates,
        traveler_role: patch.travelerRole ?? null,
        meal_type: patch.mealType ?? null,
        review_status: "confirmed" as const,
        source_type: "manual" as const,
        remark: patch.remark ?? `费用调整新增：${entry.sourceText}`,
        expected_before: null,
        after: {
          quantity: pricing.normalized.quantity,
          billing_count: pricing.normalized.billingCount,
          unit: String(patch.unit ?? entry.unit),
          spec_unit: specUnit,
          pricing_unit_type: String(patch.pricingUnitType ?? "unit"),
          unit_price_amount: pricing.normalized.unitPriceAmount,
          amount: pricing.amount,
        },
      };
    }));
  }

  return (
    <FulfillmentOrderQuickEntryPanel
      disabled={disabled || !["finalized", "effective"].includes(workspaceStatus)}
      requireSettlementSupplier
      workspaceStatus={workspaceStatus}
      itineraryDays={itineraryDays}
      serviceProviderOptions={serviceProviderOptions}
      settlementSupplierOptions={settlementSupplierOptions}
      serviceProviderResourceType={serviceProviderResourceType}
      serviceProviderSearchKeyword={serviceProviderSearchKeyword}
      serviceProviderLoading={serviceProviderLoading}
      settlementSupplierLoading={settlementSupplierLoading}
      serviceProviderError={serviceProviderError}
      settlementSupplierError={settlementSupplierError}
      serviceProviderErrorCode={serviceProviderErrorCode}
      settlementSupplierErrorCode={settlementSupplierErrorCode}
      onRetryServiceProviders={onRetryServiceProviders}
      onRetrySettlementSuppliers={onRetrySettlementSuppliers}
      onResourceTypeChange={(resourceType: ConfirmationEntryResourceType) => {
        onServiceProviderResourceTypeChange?.(resourceType);
      }}
      onSearchServiceProviders={onSearchServiceProviders}
      onSearchSettlementSuppliers={onSearchSettlementSuppliers}
      searchResourceCandidates={searchResourceCandidates}
      searchTransportPoints={searchTransportPoints}
      searchTransportResourceCandidates={searchTransportResourceCandidates}
      onAdd={addAdjustmentEntries}
      onPendingTextChange={onPendingTextChange}
    />
  );
}
